import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Menu, X, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import { PAGES, NAV_GROUPS, NAV_HIDDEN_FROM_LIST, pathFor, getPageById } from "@/data/pages";
import { PAGE_IMAGES } from "@/data/pages";
import SearchOverlay from "@/components/SearchOverlay";

const ABS_LOGO = "/images/abs-logo.png";

export default function Header() {
  const { lang, t } = useLang();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on outside click / Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpenGroup(null);
    const onClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpenGroup(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, []);

  // Close mega-menu on route change
  useEffect(() => {
    setOpenGroup(null);
    setMobileOpen(false);
    setMobileGroup(null);
  }, [location.pathname]);

  const switchLang = () => {
    const path = location.pathname;
    const isZh = path.startsWith("/zn");
    if (isZh) return path.replace(/^\/zn\/?/, "/").replace(/\/$/, "") || "/";
    return path === "/" ? "/zn" : `/zn${path}`;
  };

  const groups = [
    { id: "services", label: t.groupLabels.services },
    { id: "tax", label: t.groupLabels.tax },
    { id: "compliance", label: t.groupLabels.compliance },
    { id: "advisory", label: t.groupLabels.advisory },
    { id: "insights", label: t.groupLabels.insights },
  ];

  const isOpen = openGroup !== null;

  return (
    <header
      data-testid="site-header"
      ref={headerRef}
      className={`sticky top-0 z-50 bg-white ${lang === "zh" ? "lang-zh" : ""}`}
    >
      <div className={`relative ${isOpen ? "border-b-0" : "border-b border-[var(--abs-line)]"}`}>
        <div className={`max-w-[1480px] mx-auto px-5 lg:px-10 grid grid-cols-[auto_1fr_auto] items-center gap-6 transition-[height] duration-300 ${scrolled ? "h-[56px] lg:h-[64px]" : "h-[60px] lg:h-[88px]"}`}>
          {/* Logo */}
          <Link to={pathFor("home", lang)} data-testid="logo-link" className="flex items-center shrink-0">
            <img
              src={ABS_LOGO}
              alt="ABS Solutions"
              className={`w-auto object-contain transition-[height] duration-300 ${scrolled ? "h-[30px] lg:h-8" : "h-9 lg:h-[2.6rem]"}`}
              data-testid="logo-img"
            />
          </Link>

          {/* Center nav */}
          <nav className="hidden lg:flex items-stretch justify-center gap-0 h-full" data-testid="primary-nav">
            {groups.map((g) => (
              <button
                key={g.id}
                data-testid={`nav-group-${g.id}`}
                onClick={() => setOpenGroup(openGroup === g.id ? null : g.id)}
                className={`relative px-4 xl:px-5 h-full text-[15px] font-medium tracking-tight whitespace-nowrap transition-colors ${openGroup === g.id ? "text-[var(--abs-ink)]" : "text-[var(--abs-ink)] hover:text-[var(--abs-orange)]"}`}
              >
                <span className="inline-flex items-center gap-1">{g.label}</span>
                <span
                  className={`absolute left-1/2 -translate-x-1/2 bottom-0 h-[3px] bg-[var(--abs-orange)] transition-all ${openGroup === g.id ? "w-[34px] opacity-100" : "w-0 opacity-0"}`}
                />
              </button>
            ))}
            <Link to={pathFor("about", lang)} className="px-4 xl:px-5 h-full grid place-items-center text-[15px] font-medium whitespace-nowrap hover:text-[var(--abs-orange)]" data-testid="nav-about">{t.nav.about}</Link>
            <Link to={pathFor("services-pricing", lang)} className="px-4 xl:px-5 h-full grid place-items-center text-[15px] font-medium whitespace-nowrap hover:text-[var(--abs-orange)]" data-testid="nav-pricing">{t.nav.pricing}</Link>
            <Link to={pathFor("contact", lang)} className="px-4 xl:px-5 h-full grid place-items-center text-[15px] font-medium whitespace-nowrap hover:text-[var(--abs-orange)]" data-testid="nav-contact">{t.nav.contact}</Link>
          </nav>

          {/* Right utilities */}
          <div className="flex items-center gap-6">
            <Link to={switchLang()} data-testid="lang-switcher" className="hidden md:flex items-center gap-1.5 text-[14px] hover:text-[var(--abs-orange)] transition-colors">
              <MapPin className="h-4 w-4" />
              <span className="font-medium">{lang === "en" ? t.misc.languageZH : t.misc.languageEN}</span>
              <ChevronDown className="h-3 w-3" />
            </Link>
            <button onClick={() => setSearchOpen(true)} data-testid="search-btn" aria-label={t.nav.search} className="hidden md:flex items-center gap-1.5 text-[14px] hover:text-[var(--abs-orange)]">
              <Search className="h-4 w-4" />
              <span>{t.nav.search}</span>
            </button>
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="lg:hidden p-1.5 -mr-1.5" aria-label={t.nav.menu}>
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Mega menu panel — slides down */}
        {isOpen && (
          <div
            className="absolute left-0 right-0 top-full bg-[#F7F3EE] border-t border-[var(--abs-line)] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)] animate-[fadeDown_.22s_ease-out]"
            data-testid="mega-menu"
          >
            <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-8">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="serif text-[28px]">{groups.find((g) => g.id === openGroup)?.label}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1">
                  <Link
                    to={pathFor("services-pricing", lang)}
                    className="flex items-center justify-between py-4 border-b border-[var(--abs-line)] text-[16px] hover:text-[var(--abs-orange)] transition-colors group"
                    data-testid="megamenu-see-all"
                  >
                    <span>{lang === "zh" ? "查看所有服务" : "See all services"}</span>
                    <ChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                  </Link>
                  {NAV_GROUPS[openGroup].filter((id) => !NAV_HIDDEN_FROM_LIST.has(id)).map((id) => (
                    <Link
                      key={id}
                      to={pathFor(id, lang)}
                      data-testid={`megamenu-${id}`}
                      className="flex items-center justify-between py-4 border-b border-[var(--abs-line)] text-[16px] hover:text-[var(--abs-orange)] transition-colors group"
                    >
                      <span>{t.pageNames[id]}</span>
                      <ChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                    </Link>
                  ))}
                  {(openGroup === "advisory" || openGroup === "insights") && (
                    <Link
                      to={pathFor("insights", lang)}
                      data-testid="megamenu-see-more-insights"
                      className="flex items-center gap-3 mt-6 text-[14px] uppercase tracking-[0.18em] text-[var(--abs-orange)] hover:gap-4 transition-all"
                    >
                      <span>{lang === "zh" ? "查看所有专业洞察" : "See more insights"}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>

              <div className="col-span-12 lg:col-span-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-ink)]/70 mb-5">
                  {lang === "zh" ? "精选" : "Featured"}
                </div>
                <div className="space-y-5">
                  {NAV_GROUPS[openGroup].slice(0, 2).map((id) => {
                    const page = getPageById(id);
                    const featLabel = t.featuredLabels?.[id] || t.pageNames[id];
                    return (
                      <Link key={`feat-${id}`} to={pathFor(id, lang)} className="flex items-center gap-4 group" data-testid={`megamenu-featured-${id}`}>
                        <div className="img-frame w-[100px] h-[100px] bg-white shrink-0">
                          <img src={PAGE_IMAGES[id]} alt={featLabel} className="w-full h-full object-cover" />
                        </div>
                        <div className="serif text-[18px] leading-snug group-hover:text-[var(--abs-orange)] transition-colors">
                          {featLabel}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              onClick={() => setOpenGroup(null)}
              data-testid="megamenu-close"
              aria-label="Close"
              className="absolute top-6 right-6 lg:right-10 p-2 hover:text-[var(--abs-orange)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile drawer — PwC style */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white overflow-y-auto" data-testid="mobile-drawer">
          <div className="px-5 h-[60px] flex items-center justify-between border-b border-[var(--abs-line)]">
            <Link to={pathFor("home", lang)} onClick={() => setMobileOpen(false)} className="flex items-center">
              <img src={ABS_LOGO} alt="ABS Solutions" className="h-9 w-auto object-contain" />
            </Link>
            <button data-testid="mobile-close" onClick={() => setMobileOpen(false)} aria-label={t.nav.close}>
              <X className="h-6 w-6" />
            </button>
          </div>

          {mobileGroup === null ? (
            <div className="px-5 divide-y divide-[var(--abs-line)]">
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setMobileGroup(g.id)}
                  data-testid={`mob-group-${g.id}`}
                  className="w-full flex items-center justify-between py-5 text-left serif text-[22px]"
                >
                  <span>{g.label}</span>
                  <ChevronRight className="h-5 w-5 text-[var(--abs-orange)]" />
                </button>
              ))}
              <Link to={pathFor("about", lang)} onClick={() => setMobileOpen(false)} className="w-full flex items-center justify-between py-5 serif text-[22px]" data-testid="mob-nav-about">
                <span>{t.nav.about}</span><ChevronRight className="h-5 w-5 text-[var(--abs-orange)]" />
              </Link>
              <Link to={pathFor("services-pricing", lang)} onClick={() => setMobileOpen(false)} className="w-full flex items-center justify-between py-5 serif text-[22px]" data-testid="mob-nav-pricing">
                <span>{t.nav.pricing}</span><ChevronRight className="h-5 w-5 text-[var(--abs-orange)]" />
              </Link>
              <Link to={pathFor("contact", lang)} onClick={() => setMobileOpen(false)} className="w-full flex items-center justify-between py-5 serif text-[22px]" data-testid="mob-nav-contact">
                <span>{t.nav.contact}</span><ChevronRight className="h-5 w-5 text-[var(--abs-orange)]" />
              </Link>
              <div className="py-5 flex items-center gap-6 text-[15px]">
                <Link to={switchLang()} onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {lang === "en" ? t.misc.languageZH : t.misc.languageEN}
                </Link>
                <button onClick={() => { setMobileOpen(false); setSearchOpen(true); }} className="inline-flex items-center gap-1.5" data-testid="mob-search-btn"><Search className="h-4 w-4" /> {t.nav.search}</button>
              </div>
            </div>
          ) : (
            <div className="px-5">
              <button onClick={() => setMobileGroup(null)} className="flex items-center gap-2 py-5 text-[14px] text-[var(--abs-orange)]" data-testid="mob-back">
                <ChevronRight className="h-4 w-4 rotate-180" /> {lang === "zh" ? "返回" : "Back"}
              </button>
              <h3 className="serif text-[28px] mb-4">{groups.find((g) => g.id === mobileGroup)?.label}</h3>
              <div className="divide-y divide-[var(--abs-line)]">
                {NAV_GROUPS[mobileGroup].filter((id) => !NAV_HIDDEN_FROM_LIST.has(id)).map((id) => (
                  <Link key={id} to={pathFor(id, lang)} onClick={() => setMobileOpen(false)} data-testid={`mob-nav-${id}`} className="block py-4 text-[16px] hover:text-[var(--abs-orange)]">
                    {t.pageNames[id]}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
